// src/index.ts
function telemetryPath(value) {
  try {
    return new URL(value, "https://app.invalid").pathname.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "[id]").replace(/\/\d+(?=\/|$)/g, "/[id]");
  } catch {
    return "/unknown";
  }
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function safeUrl(value) {
  try {
    const url = new URL(value);
    return `${url.origin}${telemetryPath(value)}`;
  } catch {
    return telemetryPath(value);
  }
}
var allowedProperties = /* @__PURE__ */ new Set([
  "token",
  // Public project token: posthog-js requires it for ingestion.
  "distinct_id",
  "$device_id",
  "$user_id",
  "$anon_distinct_id",
  "$session_id",
  "$window_id",
  "$lib",
  "$lib_version",
  "$browser",
  "$browser_version",
  "$os",
  "$os_version",
  "$device_type",
  "$screen_height",
  "$screen_width",
  "$viewport_height",
  "$viewport_width",
  "$is_identified",
  "$process_person_profile",
  "$time",
  "$event_type",
  "$pageview_id",
  "$prev_pageview_id",
  "$prev_pageview_duration",
  "$prev_pageview_max_scroll_percentage",
  "$prev_pageview_last_scroll_percentage",
  "$prev_pageview_max_content_percentage",
  "$prev_pageview_last_content_percentage",
  "$exception_level",
  "$exception_handled",
  "$exception_synthetic",
  "$release_id",
  "environment",
  "service",
  "release",
  "operation",
  "route",
  "method",
  "status",
  "resource",
  "action",
  "successful_count",
  "failed_count"
]);
var allowedFrameKeys = /* @__PURE__ */ new Set([
  "platform",
  "filename",
  "abs_path",
  "function",
  "module",
  "lineno",
  "colno",
  "in_app",
  "chunk_id",
  "debug_id"
]);
function safeFrame(frame) {
  return Object.fromEntries(
    Object.entries(frame).filter(([key]) => allowedFrameKeys.has(key)).map(([key, val]) => [
      key,
      (key === "filename" || key === "abs_path" || key === "module") && typeof val === "string" ? val.split(/[?#]/)[0] : val
    ])
  );
}
function safeExceptions(value) {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((exception) => {
    const stack = isRecord(exception.stacktrace) ? exception.stacktrace : {};
    const frames = Array.isArray(stack.frames) ? stack.frames : [];
    return {
      type: typeof exception.type === "string" && /^[A-Za-z][\w.]{0,80}$/.test(exception.type) ? exception.type : "Error",
      // Error messages can contain SQL parameters, names, CUITs, or ARCA payloads.
      value: "Application error (message omitted)",
      mechanism: {
        type: "generic",
        handled: isRecord(exception.mechanism) ? exception.mechanism.handled !== false : true
      },
      stacktrace: {
        ..."type" in stack ? { type: stack.type } : {},
        frames: frames.filter(isRecord).map(safeFrame)
      }
    };
  });
}
function sanitizeProperties(properties) {
  const safe = { $geoip_disable: true, $ip: null };
  for (const [key, value] of Object.entries(properties)) {
    if (key === "$exception_list") safe[key] = safeExceptions(value);
    else if (key === "$current_url" && typeof value === "string") safe[key] = safeUrl(value);
    else if (["$pathname", "$prev_pageview_pathname"].includes(key) && typeof value === "string") {
      safe[key] = telemetryPath(value);
    } else if (allowedProperties.has(key)) safe[key] = value;
  }
  return safe;
}
function telemetryError(error, operation) {
  const safe = new Error(operation);
  if (error instanceof Error && error.stack) {
    const frames = error.stack.split("\n").filter((line) => /^\s+at /.test(line));
    safe.stack = `${safe.name}: ${operation}
${frames.join("\n")}`;
  }
  return safe;
}
function sanitizeEvent(event) {
  return {
    ...event,
    properties: sanitizeProperties(event.properties || {}),
    $set: void 0,
    $set_once: void 0
  };
}
export {
  sanitizeEvent,
  sanitizeProperties,
  telemetryError,
  telemetryPath
};
//# sourceMappingURL=index.js.map